// src/i18n/LanguageContext.js
import { createContext, useContext, useState, useCallback } from 'react';

export const LANGUAGES = {
  fa: {
    code: 'fa',
    name: 'فارسی',
    dir: 'rtl',
    fontFamily: "'Vazirmatn', Tahoma, sans-serif",
  },
  en: {
    code: 'en',
    name: 'English',
    dir: 'ltr',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('cc_lang') || 'fa';
  });

  const switchLang = useCallback((code) => {
    setLang(code);
    localStorage.setItem('cc_lang', code);
    document.documentElement.lang = code;
    document.documentElement.dir = LANGUAGES[code].dir;
  }, []);

  // Returns the correct field value based on current language
  // e.g. t(item, 'title') => item.title_fa or item.title_en
  const t = useCallback(
    (obj, field, fallback = '') => {
      if (!obj) return fallback;
      const key = `${field}_${lang}`;
      const fallbackKey = `${field}_${lang === 'fa' ? 'en' : 'fa'}`;
      return obj[key] || obj[fallbackKey] || obj[field] || fallback;
    },
    [lang]
  );

  // Simple key-based translation for UI labels
  const ui = useCallback(
    (fa, en) => (lang === 'fa' ? fa : en),
    [lang]
  );

  const value = {
    lang,
    switchLang,
    t,
    ui,
    isRTL: lang === 'fa',
    dir: LANGUAGES[lang].dir,
    fontFamily: LANGUAGES[lang].fontFamily,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used inside LanguageProvider');
  return ctx;
}
